import { GameStatus, GuessHistory, ValidatedGuess, SavedGameData, PreviousWord, KeyboardStatus } from "@/lib/types/GameTypes";
import { modeAwareStorage } from "@/lib/managers/ModeAwareStorageManager";
import { getEffectiveDate, formatDate, waitForDailyResetBarrier } from "@/lib/dateUtils";
import { getWordForDay } from "@/lib/wordLibrary";
import { atomicGameStorage } from "@/lib/storage/AtomicGameStorage";
import { GameMode } from "@/contexts/GameModeContext";

/**
 * Atomic game persistence using single-cookie operations
 * Eliminates race conditions through true atomicity
 */

export const hasInProgressGame = (): boolean => {
  // Capture mode ONCE to prevent race conditions
  const mode = (localStorage.getItem('babblegram_game_mode') as GameMode) || 'daily';
  const today = formatDate(getEffectiveDate());
  
  // Try to migrate legacy data first
  atomicGameStorage.migrateFromLegacy(mode);
  
  // Read atomic state
  const state = atomicGameStorage.read(mode);
  
  if (!state) {
    console.log('[GamePersistenceV2] No atomic state found');
    return false;
  }
  
  console.log('[GamePersistenceV2] Checking for in-progress game:', {
    gameStatus: state.gameStatus,
    savedDate: state.persistenceDate,
    currentWord: state.currentWord,
    currentLevel: state.currentLevel,
    today,
    mode,
    effectiveDate: getEffectiveDate().toISOString()
  });
  
  const hasActiveGame = Boolean(
    state.persistenceDate === today && 
    state.gameStatus === 'playing' && 
    state.currentWord && 
    state.currentLevel > 0
  );
                        
  console.log('[GamePersistenceV2] Has active game:', hasActiveGame);
  return hasActiveGame;
};

export const getEffectiveGameStatus = (): GameStatus => {
  return modeAwareStorage.getGameStatus() || 'playing';
};

export const loadGameState = async (forceRefresh: boolean = false): Promise<SavedGameData | null> => {
  // Wait for any daily reset to complete before loading
  await waitForDailyResetBarrier();
  
  try {
    // CRITICAL: Capture mode ONCE at function start to prevent race conditions
    const capturedMode = (localStorage.getItem('babblegram_game_mode') as GameMode) || 'daily';
    const today = formatDate(getEffectiveDate());
    
    console.log('[GamePersistenceV2] 📖 LOADING GAME STATE:', {
      today,
      forceRefresh,
      mode: capturedMode,
      timestamp: new Date().toISOString()
    });

    // Try to migrate legacy data first
    atomicGameStorage.migrateFromLegacy(capturedMode);

    // ATOMIC READ: Single operation, no race conditions
    const atomicState = atomicGameStorage.read(capturedMode);
    
    if (!atomicState) {
      console.log('[GamePersistenceV2] No atomic state found');
      return null;
    }

    const { persistenceDate: savedDate, currentWord, currentLevel, gameStatus } = atomicState;

    // CRITICAL: In DAILY mode, never load state from a previous day
    if (capturedMode === 'daily' && savedDate !== today) {
      console.log('[GamePersistenceV2] Saved date does not match today in DAILY mode. Clearing stale state.', {
        mode: capturedMode,
        savedDate,
        today
      });
      atomicGameStorage.clear(capturedMode);
      return null;
    }

    if (!currentWord || !currentLevel) {
      console.log('[GamePersistenceV2] Missing required game state data');
      return null;
    }

    // STRICT LEVEL-AWARE FILTERING: In daily mode, validate today's word
    let guessHistory: GuessHistory = atomicState.guessHistory;
    if (capturedMode === 'daily') {
      const todayWords = getWordForDay(getEffectiveDate());
      const expectedWord = todayWords[currentLevel - 1];
      if (!expectedWord || expectedWord !== currentWord) {
        console.log('[GamePersistenceV2] Daily mode word mismatch detected', {
          currentLevel,
          currentWord,
          expectedWord,
          gameStatus: atomicState.gameStatus,
          savedDate,
        });
        
        // If the game was already completed today, preserve completion state
        // This handles the case where a mid-day fix changes the expected word
        if ((atomicState.gameStatus === 'won' || atomicState.gameStatus === 'lost') && savedDate === today) {
          console.log('[GamePersistenceV2] Word mismatch but game completed today - preserving completion state');
          
          // Sync completion to legacy cookies
          try {
            modeAwareStorage.setGameStatus(atomicState.gameStatus);
            modeAwareStorage.setGuessHistory(atomicState.guessHistory || []);
            modeAwareStorage.setLevelAttempts(atomicState.levelAttempts || []);
            modeAwareStorage.setPersistenceDate(today);
            modeAwareStorage.setCookie('game_completion_state', 'true');
          } catch (e) {
            console.warn('[GamePersistenceV2] Legacy sync for completion preservation failed:', e);
          }
          
          atomicGameStorage.clear(capturedMode);
          return {
            currentLevel: currentLevel,
            currentWord: expectedWord || currentWord,
            guesses: [],
            guessHistory: atomicState.guessHistory || [],
            levelAttempts: atomicState.levelAttempts || [],
            gameStatus: atomicState.gameStatus,
            keyboardStatus: {} as KeyboardStatus,
            date: today,
            currentGuessRow: 0,
            previousWord: null as any,
            isTransitioning: false,
            completedLevels: [],
            timestamp: new Date().toISOString(),
          };
        }
        
        atomicGameStorage.clear(capturedMode);
        return null;
      }
      
      // STRICT FILTERING: Only keep entries that match today's words
      const originalHistoryLength = guessHistory.length;
      guessHistory = guessHistory.filter(h => {
        const expectedForLevel = todayWords[h.level - 1];
        const isValid = !!expectedForLevel && h.word === expectedForLevel && h.level >= 1 && h.level <= 4;
        if (!isValid) {
          console.log('📅 FILTERING OUT: Invalid guess history entry', {
            entryLevel: h.level,
            entryWord: h.word,
            expectedWordForLevel: expectedForLevel
          });
        }
        return isValid;
      });
      
      if (guessHistory.length !== originalHistoryLength) {
        console.log('📅 STRICT FILTERING: Removed invalid daily entries', {
          original: originalHistoryLength,
          kept: guessHistory.length,
          removed: originalHistoryLength - guessHistory.length
        });
      }
    }

    // Create state object
    const state: SavedGameData = {
      guessHistory,
      levelAttempts: atomicState.levelAttempts,
      gameStatus: atomicState.gameStatus,
      currentWord: atomicState.currentWord,
      currentLevel: atomicState.currentLevel,
      guesses: [],
      currentGuessRow: atomicState.currentGuessRow,
      keyboardStatus: atomicState.keyboardStatus,
      previousWord: atomicState.previousWord,
      isTransitioning: atomicState.isTransitioning,
      completedLevels: atomicState.completedLevels,
      date: savedDate,
      timestamp: new Date().toISOString()
    };

    // Sync atomic state to legacy cookies for components that still read from them
    try {
      modeAwareStorage.setGuessHistory(guessHistory);
      modeAwareStorage.setLevelAttempts(atomicState.levelAttempts);
      modeAwareStorage.setGameStatus(atomicState.gameStatus);
      modeAwareStorage.setCurrentWord(atomicState.currentWord);
      modeAwareStorage.setCurrentLevel(atomicState.currentLevel);
      modeAwareStorage.setPersistenceDate(savedDate);
      if (atomicState.previousWord) {
        modeAwareStorage.setPreviousWord(atomicState.previousWord);
      }
      if (atomicState.gameStatus === 'won' || atomicState.gameStatus === 'lost') {
        modeAwareStorage.setCookie('game_completion_state', 'true');
      }
    } catch (syncError) {
      console.warn('[GamePersistenceV2] Legacy sync on load failed:', syncError);
    }

    // Get current level's guesses from guess history
    let currentGuesses: ValidatedGuess[] = [];
    if (guessHistory && guessHistory.length > 0) {
      const currentLevelHistory = guessHistory.find(
        h => h.level === currentLevel && h.word === currentWord
      );
      
      if (currentLevelHistory && currentLevelHistory.guesses && currentLevelHistory.guesses.length > 0) {
        console.log('[GamePersistenceV2] Found level-specific guesses in history:', {
          level: currentLevel,
          word: currentWord,
          guessCount: currentLevelHistory.guesses.length
        });
        currentGuesses = JSON.parse(JSON.stringify(currentLevelHistory.guesses));
      }
    }

    state.guesses = currentGuesses;

    console.log('[GamePersistenceV2] Successfully loaded game state:', {
      currentWord: state.currentWord,
      currentLevel: state.currentLevel,
      gameStatus: state.gameStatus,
      guessCount: state.guesses?.length || 0,
      mode: capturedMode
    });

    return state;
  } catch (error) {
    console.error('[GamePersistenceV2] Error loading game state:', error);
    return null;
  }
};

export const saveGameState = async (
  guessHistory: GuessHistory,
  levelAttempts: number[],
  gameStatus: GameStatus,
  currentWord: string,
  currentLevel: number,
  guesses: ValidatedGuess[],
  currentGuessRow: number,
  keyboardStatus: KeyboardStatus,
  previousWord: PreviousWord,
  isTransitioning: boolean = false
): Promise<void> => {
  // Wait for any daily reset to complete before saving
  await waitForDailyResetBarrier();
  
  try {
    // Prevent writes during mode switch to avoid cross-mode contamination
    const isModeSwitching = localStorage.getItem('babblegram_mode_switch_in_progress') === '1';
    if (isModeSwitching) {
      console.log('[GamePersistenceV2] Mode switch in progress - skipping save');
      return;
    }

    // Guard: Skip persistence during debug date change
    const isDebugDateChanging = localStorage.getItem('babblegram_debug_date_change_in_progress') === 'true';
    if (isDebugDateChanging) {
      console.log('[GamePersistenceV2] Debug date change in progress - skipping save');
      return;
    }

    if (!currentWord) {
      console.log('[GamePersistenceV2] No current word provided, skipping save');
      return;
    }

    // CRITICAL: Capture mode ONCE at function start to prevent race conditions
    const capturedMode = (localStorage.getItem('babblegram_game_mode') as GameMode) || 'daily';
    const validLevel = Math.max(1, Math.min(4, currentLevel));
    const today = formatDate(getEffectiveDate());
    
    // Read existing atomic state (if any)
    const existingState = atomicGameStorage.read(capturedMode);

    // ATOMIC GUARD: In daily mode, clear stale data if date mismatch
    if (capturedMode === 'daily' && existingState && existingState.persistenceDate !== today) {
      console.log('[saveGameState] Date mismatch in daily mode, clearing stale data', {
        existingDate: existingState.persistenceDate,
        today,
        mode: capturedMode
      });
      atomicGameStorage.clear(capturedMode);
    }

    // In DAILY mode, ensure we only persist for today's word
    if (capturedMode === 'daily') {
      const wordsToday = getWordForDay(getEffectiveDate());
      const expectedWord = wordsToday[validLevel - 1];
      if (expectedWord && expectedWord !== currentWord) {
        console.log('[GamePersistenceV2] Daily mode word mismatch during save - skipping', {
          validLevel,
          currentWord,
          expectedWord,
        });
        return;
      }
    }

    // Determine effective status (don't override won/lost)
    const existingStatus = existingState?.gameStatus;
    const isPrevStale = capturedMode === 'daily' && existingState?.persistenceDate !== today;
    const carryOverStatus = isPrevStale ? null : existingStatus;
    const effectiveStatus = (carryOverStatus === 'won' || carryOverStatus === 'lost') ? carryOverStatus : gameStatus;

    // Build effective guess history
    const existingGuessHistory = existingState?.guessHistory || [];
    let effectiveGuessHistory = [...existingGuessHistory];
    
    // Filter for daily mode
    if (capturedMode === 'daily') {
      const wordsToday = getWordForDay(getEffectiveDate());
      effectiveGuessHistory = effectiveGuessHistory.filter(entry => {
        const expectedWordForLevel = wordsToday[entry.level - 1];
        return entry.level >= 1 && entry.level <= 4 && 
               expectedWordForLevel && entry.word === expectedWordForLevel;
      });
    }

    // Merge new guesses
    if (guesses.length > 0) {
      const existingLevelIndex = effectiveGuessHistory.findIndex(h => 
        h.level === validLevel && h.word === currentWord
      );
      
      if (existingLevelIndex >= 0) {
        effectiveGuessHistory[existingLevelIndex] = {
          level: validLevel,
          word: currentWord,
          guesses: JSON.parse(JSON.stringify(guesses))
        };
      } else {
        effectiveGuessHistory.push({
          level: validLevel,
          word: currentWord,
          guesses: JSON.parse(JSON.stringify(guesses))
        });
      }
    } else if (!existingGuessHistory.length) {
      effectiveGuessHistory = guessHistory;
    }

    const effectiveLevelAttempts = existingState?.levelAttempts.length ? 
      existingState.levelAttempts : levelAttempts;
    
    console.log('[GamePersistenceV2] 💾 SAVING ATOMIC GAME STATE:', {
      currentWord,
      validLevel,
      effectiveStatus,
      guessCount: guesses.length,
      historyEntries: effectiveGuessHistory.length,
      mode: capturedMode,
      timestamp: new Date().toISOString()
    });

    // ATOMIC WRITE: Single operation, no race conditions!
    const success = atomicGameStorage.write(capturedMode, {
      currentWord,
      currentLevel: validLevel,
      gameStatus: effectiveStatus,
      guessHistory: effectiveGuessHistory,
      levelAttempts: effectiveLevelAttempts,
      keyboardStatus,
      previousWord,
      currentGuessRow,
      isTransitioning,
      completedLevels: existingState?.completedLevels || [],
      persistenceDate: today,
      lastPlayed: today,
      completionState: effectiveStatus === 'won' || effectiveStatus === 'lost'
    });

    if (!success) {
      throw new Error('Failed to write atomic state (possible size limit exceeded)');
    }

    // Sync to legacy cookies for backward-compatible reads
    try {
      modeAwareStorage.setGuessHistory(effectiveGuessHistory);
      modeAwareStorage.setLevelAttempts(effectiveLevelAttempts);
      modeAwareStorage.setGameStatus(effectiveStatus);
      modeAwareStorage.setCurrentWord(currentWord);
      modeAwareStorage.setCurrentLevel(validLevel);
      modeAwareStorage.setPersistenceDate(today);
      if (previousWord) {
        modeAwareStorage.setPreviousWord(previousWord);
      }
      if (effectiveStatus === 'won' || effectiveStatus === 'lost') {
        modeAwareStorage.setCookie('game_completion_state', 'true');
      }
    } catch (syncError) {
      console.warn('[GamePersistenceV2] Legacy cookie sync failed (non-fatal):', syncError);
    }

  } catch (error) {
    console.error('[GamePersistenceV2] Error saving game state:', error);
    throw error;
  }
};

export const clearGameState = async (): Promise<void> => {
  console.log('[GamePersistenceV2] Clearing game state');
  
  try {
    // Capture mode to prevent race conditions
    const capturedMode = (localStorage.getItem('babblegram_game_mode') as GameMode) || 'daily';
    
    // ATOMIC CLEAR: Single operation
    atomicGameStorage.clear(capturedMode);
    
    // Also clear via mode-aware storage for backwards compatibility
    modeAwareStorage.clearModeState();
    
    console.log('[GamePersistenceV2] Atomic game state cleared for mode:', capturedMode);
    modeAwareStorage.clearModeState();
    console.log('[GamePersistenceV2] Game state cleared successfully');
  } catch (error) {
    console.error('[GamePersistenceV2] Error during game state clear:', error);
    throw error;
  }
};